import { useEffect, useState } from "react";
import { DataCard, DataCardRow } from "../DataCard/DataCard";
import { BACKEND_AI_AGENT_URL } from "../../../../../config";
import { abbreviateNumber } from "../../../../../utils/utils";
import { AdvancedChart } from "react-tradingview-embed";
import { useParams } from "react-router-dom";

interface TechnicalAnalysisData {
  ath: number;
  atl: number;
  currentPrice: number;
  percentageFromAth: number;
  percentageFromAtl: number;
  marketCap: number;
  fullyDilutedValuation: number;
  totalSupplyInCirculationPercentage: number;
  dexLiquidity: number;
  dexLiquidityPercentage: number;
  dailyVolume: number;
  weeklyAvgVolume: number;
  circulatingSupply: number;
  recentPerformancePercentage: number;
  recentPerformancePercentageVsBTC: number;
  volumeMarketCapRatio: number;
  tradingViewSymbol: string;
}

export const TechnicalAnalysis = ({ projectData }) => {
  const [data, setData] = useState<TechnicalAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { projectId } = useParams();

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        const response = await fetch(
          `${BACKEND_AI_AGENT_URL}/technical-analysis?projectId=${projectId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const data: TechnicalAnalysisData = await response.json();

        setData(data);
      } catch (error) {
        console.error("Failed to fetch trending data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrendingData();
  }, [projectId]);

  if (!projectData) return <></>;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!data) {
    return <div>No data available</div>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        {data?.tradingViewSymbol && (
          <AdvancedChart
            widgetProps={{
              symbol: data.tradingViewSymbol,
              interval: "240",
              height: "400",
              hide_side_toolbar: true,
              allow_symbol_change: false,
              withdateranges: false,
            }}
          />
        )}
      </div>

      <div className="flex gap-5 flex-col sm:flex-row">
        <DataCard
          title="Price Action & Volatility"
          gatewayScore={
            projectData?.detailedComponentAnalysis?.technicalAnalysis
              ?.priceActionAndVolatility?.score
          }
          premium
        >
          <DataCardRow
            title={`Currently trading at ${data.currentPrice.toFixed(3)} USD`}
            valueElement={
              <p className="font-medium text-[#4CAF50]">
                -{data.percentageFromAth.toFixed(1)}%{" "}
                <span className="text-white opacity-50 text-xs">
                  from ATH ({data.ath.toFixed(4)} USD)
                </span>
              </p>
            }
          />
          <DataCardRow
            title="Shows resilience with"
            valueElement={
              <p className="font-medium text-[#4CAF50]">
                +{data.percentageFromAtl.toFixed(1)}%{" "}
                <span className="text-white opacity-50 text-xs">
                  from ATL ({data.atl.toFixed(4)} USD)
                </span>
              </p>
            }
          />
          <DataCardRow
            title="Recent performance"
            valueElement={
              <>
                <p className="font-medium text-white">
                  <span
                    className={
                      data.recentPerformancePercentage >= 0
                        ? "text-[#4CAF50]"
                        : "text-[#F44336]"
                    }
                  >
                    {data.recentPerformancePercentage.toFixed(1)}%
                  </span>{" "}
                  <span className="opacity-50 text-xs">24h</span>{" "}
                  <span
                    className={
                      data.recentPerformancePercentageVsBTC >= 0
                        ? "text-[#4CAF50]"
                        : "text-[#F44336]"
                    }
                  >
                    {data.recentPerformancePercentageVsBTC.toFixed(1)}%
                  </span>{" "}
                  <span className="opacity-50 text-xs">vs BTC</span>
                </p>
                <p className="text-sm">
                  {data.recentPerformancePercentage >=
                  data.recentPerformancePercentageVsBTC
                    ? "Indicates strength"
                    : "Indicates weakness"}
                </p>
              </>
            }
          />
        </DataCard>
        <DataCard
          title="DEX Liquidity"
          premium
          gatewayScore={
            projectData?.detailedComponentAnalysis?.technicalAnalysis
              ?.volumeAndLiquidity?.score
          }
        >
          <DataCardRow
            title="DEX liquidity"
            valueElement={
              <>
                <p
                  className={`font-medium ${
                    data.dexLiquidityPercentage >= 5
                      ? "text-[#4CAF50]"
                      : "text-[#F44336]"
                  } `}
                >
                  {abbreviateNumber(data.dexLiquidity)} USD{" "}
                  <span className="text-white opacity-50 text-xs">
                    {"(Benchmark: >5% of MCap ideal)"}
                  </span>
                </p>
                <p className="text-sm">
                  Currently {data.dexLiquidityPercentage.toFixed(2)}% of MCap,
                  {data.dexLiquidityPercentage >= 5
                    ? " healthy level"
                    : " below ideal level"}
                </p>
              </>
            }
          />
          <DataCardRow
            title="Daily volume"
            valueElement={
              <p className="font-medium">
                {abbreviateNumber(data.dailyVolume)} USD{" "}
                <span className="text-white opacity-50 text-xs">24h</span>
              </p>
            }
          />
          <DataCardRow
            title="Weekly average volume"
            valueElement={
              <p className="font-medium">
                {abbreviateNumber(data.weeklyAvgVolume)} USD{" "}
                <span className="text-white opacity-50 text-xs">24h</span>
              </p>
            }
          />
          <DataCardRow
            title="Volume/Market Cap ratio"
            valueElement={
              <>
                <p
                  className={`font-medium ${
                    data.volumeMarketCapRatio > 5
                      ? "text-[#4CAF50]"
                      : data.volumeMarketCapRatio > 3
                        ? "text-[#FFEB3B]"
                        : "text-[#F44336]"
                  }`}
                >
                  {data.volumeMarketCapRatio.toFixed(2)}%
                  <span className="text-white opacity-50 text-xs">
                    {" (Benchmark: >3% healthy, >5% strong)"}
                  </span>
                </p>
                <p className="text-sm">
                  {data.volumeMarketCapRatio > 5
                    ? "Strong trading activity"
                    : data.volumeMarketCapRatio > 3
                      ? "Healthy trading activity"
                      : "Below ideal trading activity"}
                </p>
              </>
            }
          />
        </DataCard>
      </div>
      <div className="flex gap-5 flex-col sm:flex-row">
        <DataCard
          title="Supply Metrics"
          premium
          gatewayScore={
            projectData?.detailedComponentAnalysis?.technicalAnalysis
              ?.supplyMetrics?.score
          }
        >
          <DataCardRow
            title="Market Cap"
            valueElement={
              <p className="font-medium">
                {abbreviateNumber(data.marketCap)} USD
              </p>
            }
          />
          <DataCardRow
            title="FDV"
            valueElement={
              <p className="font-medium">
                {abbreviateNumber(data.fullyDilutedValuation)} USD
              </p>
            }
          />
          <DataCardRow
            title="Total supply in circulation"
            valueElement={
              <p className="font-medium text-white">
                <span
                  className={
                    data.totalSupplyInCirculationPercentage >= 70
                      ? "text-[#4CAF50]"
                      : "text-[#F44336]"
                  }
                >
                  {data.totalSupplyInCirculationPercentage.toFixed(1)}%
                </span>{" "}
                <span className="opacity-50 text-xs">
                  {"(Benchmark: >70% preferred)"}
                </span>
              </p>
            }
          />
        </DataCard>
        <DataCard
          title="Token Inflation Analysis"
          premium
          gatewayScore={
            projectData?.detailedComponentAnalysis?.technicalAnalysis
              ?.inflationMetrics?.score
          }
        >
          <DataCardRow
            title="Circulating supply"
            valueElement={
              <p className="font-medium">
                {data.circulatingSupply.toLocaleString()}{" "}
                <span className="text-white opacity-50 text-xs">
                  ({data.totalSupplyInCirculationPercentage.toFixed(1)}%)
                </span>
              </p>
            }
          />
        </DataCard>
      </div>
    </div>
  );
};
